export type PointerPoint = { x: number; y: number };
export type PointerGesture = "undecided" | "pan" | "pinch";

export type PointerGestureTransition =
  | { kind: "none" }
  | { kind: "undecided"; point: PointerPoint }
  | { kind: "pan"; point: PointerPoint; startPoint: PointerPoint }
  | { kind: "pinch"; points: PointerPoint[] }
  | { kind: "tap"; candidateIndex: number | undefined }
  | { kind: "resume-pan"; point: PointerPoint; startPoint: PointerPoint };

export const tapMovementThresholdCssPixels = 10;

export class PointerGestureClassifier {
  private readonly pointers = new Map<number, PointerPoint>();
  private _gesture: PointerGesture = "undecided";
  private _startPoint: PointerPoint | undefined;
  private pendingTap: { pointerId: number; candidateIndex: number | undefined } | undefined;

  public get pointerCount(): number {
    return this.pointers.size;
  }

  public get gesture(): PointerGesture {
    return this._gesture;
  }

  public get startPoint(): PointerPoint | undefined {
    return this._startPoint;
  }

  public hasPointer(pointerId: number): boolean {
    return this.pointers.has(pointerId);
  }

  public points(): PointerPoint[] {
    return [...this.pointers.values()].map(point => ({ ...point }));
  }

  public pointerDown(pointerId: number, point: PointerPoint, candidateIndex: number | undefined): void {
    this.pointers.set(pointerId, { ...point });
    if (this.pointers.size === 1) {
      this._startPoint = { ...point };
      this._gesture = "undecided";
      this.pendingTap = { pointerId, candidateIndex };
    } else if (this.pointers.size === 2) {
      this._startPoint = undefined;
      this._gesture = "pinch";
      this.pendingTap = undefined;
    }
  }

  public pointerMove(pointerId: number, point: PointerPoint): PointerGestureTransition {
    if (!this.pointers.has(pointerId)) return { kind: "none" };
    this.pointers.set(pointerId, { ...point });
    if (this.pointers.size === 1 && this._startPoint) {
      if (this._gesture === "undecided") {
        if (distance(point, this._startPoint) < tapMovementThresholdCssPixels) return { kind: "undecided", point: { ...point } };
        this._gesture = "pan";
        this.pendingTap = undefined;
      }
      return { kind: "pan", point: { ...point }, startPoint: { ...this._startPoint } };
    }
    if (this.pointers.size >= 2) {
      this._gesture = "pinch";
      this.pendingTap = undefined;
      return { kind: "pinch", points: this.points() };
    }
    return { kind: "none" };
  }

  public pointerUp(pointerId: number): PointerGestureTransition {
    return this.finishPointer(pointerId, false);
  }

  public pointerCancel(pointerId: number): PointerGestureTransition {
    return this.finishPointer(pointerId, true);
  }

  public reset(): void {
    this.pointers.clear();
    this._startPoint = undefined;
    this._gesture = "undecided";
    this.pendingTap = undefined;
  }

  private finishPointer(pointerId: number, cancelled: boolean): PointerGestureTransition {
    const wasTap = !cancelled
      && this.pointers.size === 1
      && this._gesture === "undecided"
      && this.pendingTap?.pointerId === pointerId;
    const candidateIndex = wasTap ? this.pendingTap?.candidateIndex : undefined;
    if (this.pointers.has(pointerId)) this.pointers.delete(pointerId);
    if (this.pointers.size === 0) {
      this.reset();
      return wasTap ? { kind: "tap", candidateIndex } : { kind: "none" };
    }
    if (this.pointers.size === 1) {
      const [point] = this.points();
      this._startPoint = { ...point };
      this._gesture = "pan";
      this.pendingTap = undefined;
      return { kind: "resume-pan", point: { ...point }, startPoint: { ...point } };
    }
    return { kind: "none" };
  }
}

function distance(left: PointerPoint, right: PointerPoint): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}
